import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from '@produzionejb/ui';
import { useCon, useUpdateCon, useCloseCon, downloadConLabels } from '../../lib/con-api';
import { useProductSkus } from '../../lib/product-skus-api';
import { LotPositionPicker } from '../../components/LotPositionPicker';
import { OperatorPicker } from '../../components/OperatorPicker';
import { BackToStockMovementsLink } from '../../components/BackToStockMovementsLink';
import type { ConLineDraft } from '@produzionejb/types';

interface FormLine {
  id?: string | null;
  lineNumber: number;
  operatorName: string;
  lotPositionId: string;
  lotLabel: string; // display "DE123 | HQ CBD BOOST | 5860g"
  lotProductId: string; // serve a filtrare gli SKU
  qtyConsumedG: string;
  productSkuId: string;
  productSkuLabel: string;
  productSkuGrammatura: number | null;
  qtyPiecesProduced: string;
  note: string;
}

// DEFAULT_OPERATORS rimosso 2026-05-22: gli operatori sono ora gestiti via
// entita' Operator nel DB. Vedi pagina /admin/operators per CRUD. Il
// dropdown CON line operator usa <OperatorPicker> che fa lookup DB.

export function ConFormPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const conQ = useCon(id ?? null);
  const updateMut = useUpdateCon(id ?? '');
  const closeMut = useCloseCon(id ?? '');

  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<FormLine[]>([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!conQ.data) return;
    setNotes(conQ.data.notes ?? '');
    setLines(
      conQ.data.lines.map((l) => ({
        id: l.id,
        lineNumber: l.lineNumber,
        operatorName: l.operatorName,
        lotPositionId: l.lotPositionId,
        lotLabel: `${l.lotCode ?? '?'} | ${l.lotProductCode ?? '?'} | ...`,
        lotProductId: '', // unknown senza fetch (basta che SKU non sia filtrato fino al cambio)
        qtyConsumedG: l.qtyConsumedG,
        productSkuId: l.productSkuId,
        productSkuLabel: l.productSkuCode ?? '',
        productSkuGrammatura: l.productSkuGrammatura,
        qtyPiecesProduced: String(l.qtyPiecesProduced),
        note: l.note ?? '',
      })),
    );
    setDirty(false);
  }, [conQ.data]);

  const isClosed = conQ.data?.status === 'CLOSED';

  function setLine(idx: number, patch: Partial<FormLine>) {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
    setDirty(true);
  }

  function addLine() {
    setLines((prev) => [
      ...prev,
      {
        id: null,
        lineNumber: prev.length + 1,
        operatorName: '',
        lotPositionId: '',
        lotLabel: '',
        lotProductId: '',
        qtyConsumedG: '',
        productSkuId: '',
        productSkuLabel: '',
        productSkuGrammatura: null,
        qtyPiecesProduced: '',
        note: '',
      },
    ]);
    setDirty(true);
  }

  function removeLine(idx: number) {
    setLines((prev) =>
      prev.filter((_, i) => i !== idx).map((l, i) => ({ ...l, lineNumber: i + 1 })),
    );
    setDirty(true);
  }

  async function handleSave(e?: FormEvent) {
    e?.preventDefault();
    const payload = {
      notes: notes || null,
      lines: lines
        .filter((l) => l.lotPositionId && l.productSkuId && l.operatorName.trim())
        .map<ConLineDraft>((l) => ({
          id: l.id ?? null,
          lineNumber: l.lineNumber,
          operatorName: l.operatorName.trim(),
          lotPositionId: l.lotPositionId,
          qtyConsumedG: Number(l.qtyConsumedG) || 0,
          productSkuId: l.productSkuId,
          qtyPiecesProduced: parseInt(l.qtyPiecesProduced, 10) || 0,
          note: l.note || null,
        })),
    };
    try {
      await updateMut.mutateAsync(payload);
      setDirty(false);
    } catch (err) {
      window.alert((err as Error).message);
    }
  }

  async function handleClose() {
    if (!window.confirm(t('con.form.confirmClose'))) return;
    if (dirty) {
      await handleSave();
    }
    try {
      await closeMut.mutateAsync();
    } catch (err) {
      window.alert((err as Error).message);
    }
  }

  if (conQ.isLoading) return <div>{t('common.loading')}</div>;
  if (conQ.error) return <div className="text-red-700">{(conQ.error as Error).message}</div>;
  if (!conQ.data) return null;

  const c = conQ.data;

  return (
    <div className="space-y-4">
      <BackToStockMovementsLink />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">
            {isClosed
              ? t('con.form.titleClosed', { code: c.code })
              : t('con.form.titleDraft')}
            <span className="ml-2 text-base font-normal text-slate-500">
              {new Date(c.workDate).toLocaleDateString('it-IT')}
            </span>
          </h2>
          <p className="text-sm text-slate-500">
            {t('con.form.summary', {
              boxes: c.totBoxesCreated,
              pieces: c.totPiecesProduced,
              consumed: (Number(c.totQtyConsumedG) / 1000).toFixed(2),
              sfrido: Number(c.totSfridoG).toFixed(1),
            })}
          </p>
        </div>
        <div className="flex gap-2">
          {isClosed && c.totBoxesCreated > 0 && id && (
            <Button
              type="button"
              variant="secondary"
              onClick={() =>
                void downloadConLabels(id).catch((err) => window.alert((err as Error).message))
              }
            >
              {t('con.form.printLabels', { count: c.totBoxesCreated })}
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={() => navigate('/documents/con')}>
            {t('common.back')}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('con.form.linesTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {lines.length === 0 && (
            <p className="text-sm text-slate-500">{t('con.form.linesEmpty')}</p>
          )}
          {lines.map((line, idx) => (
            <ConLineEditor
              key={idx}
              line={line}
              isClosed={isClosed}
              onChange={(patch) => setLine(idx, patch)}
              onRemove={() => removeLine(idx)}
            />
          ))}
          {!isClosed && (
            <button
              type="button"
              onClick={addLine}
              className="text-sm font-medium text-brand-700 hover:underline"
            >
              + {t('con.form.addLine')}
            </button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('common.actions')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label>{t('con.fields.notes')}</Label>
            <textarea
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              rows={2}
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                setDirty(true);
              }}
              disabled={isClosed}
            />
          </div>
          <div className="flex gap-2">
            {!isClosed && (
              <>
                <Button onClick={() => void handleSave()} disabled={!dirty || updateMut.isPending}>
                  {updateMut.isPending ? t('common.loading') : t('con.form.saveDraft')}
                </Button>
                <Button
                  variant="danger"
                  onClick={() => void handleClose()}
                  disabled={lines.length === 0 || closeMut.isPending}
                >
                  {closeMut.isPending ? t('common.loading') : t('con.form.closeDocument')}
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ConLineEditor({
  line,
  isClosed,
  onChange,
  onRemove,
}: {
  line: FormLine;
  isClosed: boolean;
  onChange: (patch: Partial<FormLine>) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const skusQ = useProductSkus(
    { productId: line.lotProductId || undefined, active: true },
    !!line.lotProductId,
  );

  // Derivati live (M3 -> Step 2 refactor 2026-05-20):
  // CON e' il SECONDO step del confezionamento (sfuso -> pezzi e' fatto da MPG).
  // qtyConsumedG e' DERIVATO da qtyPiecesProduced * grammatura, non piu' input utente.
  // qtyPiecesProduced deve essere multiplo di piecesPerBox (no pezzi sciolti sospesi).
  const skuSelected = line.productSkuId && skusQ.data
    ? skusQ.data.items.find((s) => s.id === line.productSkuId) ?? null
    : null;
  const grammatura = skuSelected?.grammatura ?? line.productSkuGrammatura ?? null;
  const piecesPerBox = skuSelected?.piecesPerBox ?? null;
  const pieces = parseInt(line.qtyPiecesProduced, 10) || 0;
  const theoretical = grammatura ? pieces * grammatura : 0;
  const nBoxes = piecesPerBox && piecesPerBox > 0 ? Math.floor(pieces / piecesPerBox) : 0;
  const piecesRemainder = piecesPerBox && piecesPerBox > 0 ? pieces % piecesPerBox : 0;

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase text-slate-500">
          {t('con.fields.lineNumber', { n: line.lineNumber })}
        </span>
        {!isClosed && (
          <button
            type="button"
            onClick={onRemove}
            className="text-xs font-medium text-red-600 hover:underline"
          >
            {t('common.delete')}
          </button>
        )}
      </div>
      <div className="grid gap-2 sm:grid-cols-12">
        <div className="sm:col-span-2">
          <Label>{t('con.fields.operator')}</Label>
          <OperatorPicker
            value={line.operatorName}
            onChange={(v) => onChange({ operatorName: v })}
            disabled={isClosed}
            className="block w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs"
          />
        </div>
        <div className="sm:col-span-4">
          <Label>{t('con.fields.lotPosition')}</Label>
          <LotPositionPicker
            value={line.lotPositionId}
            valueLabel={line.lotLabel || undefined}
            disabled={isClosed}
            onChange={(it) =>
              onChange({
                lotPositionId: it.id,
                lotLabel: `${it.lotCode} | ${it.productCode} | ${Number(it.residualQty).toFixed(0)}g`,
                lotProductId: it.productId,
                // Reset SKU quando cambi lotto
                productSkuId: '',
                productSkuLabel: '',
                productSkuGrammatura: null,
              })
            }
          />
        </div>
        <div className="sm:col-span-2">
          <Label>{t('con.fields.qtyConsumedG')}</Label>
          <Input
            type="number"
            inputMode="decimal"
            step="any"
            value={theoretical > 0 ? theoretical.toFixed(2) : ''}
            readOnly
            disabled
            className="bg-slate-100"
            title="Derivato: pezzi × grammatura. Lo scarico sfuso lo fa MPG."
          />
        </div>
        <div className="sm:col-span-2">
          <Label>{t('con.fields.sku')}</Label>
          <select
            className="block w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs uppercase"
            value={line.productSkuId}
            onChange={(e) => {
              const sku = skusQ.data?.items.find((s) => s.id === e.target.value);
              onChange({
                productSkuId: e.target.value,
                productSkuLabel: sku?.sku ?? '',
                productSkuGrammatura: sku?.grammatura ?? null,
              });
            }}
            disabled={isClosed || !line.lotProductId}
          >
            <option value="">—</option>
            {skusQ.data?.items.map((s) => (
              <option key={s.id} value={s.id}>
                {s.sku} ({s.grammatura}g{s.piecesPerBox ? ` × ${s.piecesPerBox}` : ''})
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <Label>{t('con.fields.qtyPiecesProduced')}</Label>
          <Input
            type="number"
            inputMode="numeric"
            step={piecesPerBox ?? 1}
            value={line.qtyPiecesProduced}
            onChange={(e) => onChange({ qtyPiecesProduced: e.target.value })}
            disabled={isClosed}
            title={piecesPerBox ? `Multiplo di ${piecesPerBox}: produrra' ${nBoxes} scatole` : undefined}
          />
          {piecesPerBox && pieces > 0 && (
            <div className="mt-0.5 text-[10px] text-slate-500">
              {nBoxes} scatole
              {piecesRemainder > 0 && (
                <span className="ml-1 rounded bg-red-100 px-1 text-red-700">
                  +{piecesRemainder} pz no scatola (non valido)
                </span>
              )}
            </div>
          )}
        </div>
        <div className="sm:col-span-12">
          <Label>{t('con.fields.note')}</Label>
          <Input
            value={line.note}
            onChange={(e) => onChange({ note: e.target.value })}
            disabled={isClosed}
          />
        </div>
      </div>
      {pieces > 0 && (
        <div className="mt-2 grid grid-cols-3 gap-2 border-t border-slate-200 pt-2 text-xs text-slate-600">
          <div>
            <span className="text-slate-500">{t('con.derived.theoretical')}: </span>
            <span className="font-medium">{theoretical.toFixed(1)}g</span>
          </div>
          <div>
            <span className="text-slate-500">Scatole: </span>
            <span className="font-medium">{nBoxes}</span>
          </div>
          {piecesRemainder > 0 && (
            <div>
              <span className="text-amber-700">
                Resto pezzi: {piecesRemainder} (non valido — usa multiplo di {piecesPerBox})
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
